import type { ContactFieldsFragment } from '@/graphql/types';
import { useEffect } from 'react';
import type { ContactFormData } from '../ContactForm/contactForm.schema';
import {
    NotificationsContainer,
    NOTIFICATION_VARIANT,
    useNotifications,
} from '../../../../notifications/Notifications';
import { ModalDialog } from '../../../../modal/layout/ModalDialog';
import { ModalFooter } from '../../../../modal/layout/ModalFooter';
import { ModalHeader } from '../../../../modal/layout/ModalHeader';
import { ContactForm } from '../ContactForm/ContactForm';
import { useContactForm } from '../ContactForm/useContactForm';
import { useProtectedClose } from '../../../../modal/hooks/useProtectedClose';

interface ContactModalProps {
    open: boolean;
    existingContact?: ContactFieldsFragment;
    draftContact?: Partial<ContactFormData>;
    onClose: () => void;
    onCreateSuccess?: (contact: ContactFieldsFragment) => void;
}

export function ContactModal({
    open,
    existingContact,
    draftContact,
    onClose,
    onCreateSuccess,
}: ContactModalProps) {
    const formId = 'contact-form';
    const title = existingContact?.id ? gettext('EDIT_CONTACT') : gettext('NEW_CONTACT');

    const { addNotification, clearNotifications } = useNotifications();
    const { register, onSubmit, isDirty, isValid, isMutating, errors } = useContactForm({
        existingContact,
        draftContact,
        onClose,
        onCreateSuccess,
    });
    const { attemptClose, isShaking, showWarning } = useProtectedClose({
        isOpen: open,
        shouldWarn: isDirty,
        onClose,
        canClose: !isMutating,
    });

    useEffect(() => {
        if (showWarning) {
            addNotification({
                message: gettext('UNSAVED_CHANGES_CLOSE_AGAIN'),
                variant: NOTIFICATION_VARIANT.WARNING,
            });
        } else {
            clearNotifications();
        }
    }, [showWarning, addNotification, clearNotifications]);

    return (
        <ModalDialog
            open={open}
            onClose={attemptClose}
            canClose={!isMutating}
            closeOnClickOutside={true}
            closeOnEsc={true}
            shake={isShaking}
        >
            <div className="relative flex flex-col py-6">
                <ModalHeader onClose={attemptClose} title={title} isLoading={isMutating} />
                <ContactForm
                    formId={formId}
                    register={register}
                    onSubmit={onSubmit}
                    errors={errors}
                />
                <div className="px-6">
                    <NotificationsContainer />
                </div>
                <hr />
                <ModalFooter
                    formId={formId}
                    onClose={attemptClose}
                    saveLabel={existingContact?.id ? gettext('UPDATE') : undefined}
                    saveButtonType="submit"
                    isLoading={isMutating}
                    saveDisabled={!isDirty || !isValid}
                />
            </div>
        </ModalDialog>
    );
}
